import * as React from "react";
import { Box, Button, Popover, Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import PageHeaderIcon from "../PageHeaderIcon";
import { C } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Leading blanks only — matches the reference two-month range picker, which
// never shows adjacent-month days, just empty cells before day 1.
function monthCells(month) {
  const cells = [];
  for (let i = 0; i < month.day(); i++) cells.push(null);
  for (let d = 1; d <= month.daysInMonth(); d++) cells.push(month.date(d));
  return cells;
}

function DayCell({ day, draftStart, draftEnd, onPick }) {
  if (!day) return <Box sx={{ width: 34, height: 34 }} />;
  const dateStr = day.format("YYYY-MM-DD");
  const isStart = dateStr === draftStart;
  const isEnd = dateStr === draftEnd;
  const isEndpoint = isStart || isEnd;
  const inRange = draftStart && draftEnd && dateStr > draftStart && dateStr < draftEnd;

  return (
    <Box
      onClick={() => onPick(dateStr)}
      sx={{
        width: 34,
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: "0.8125rem",
        borderRadius: isEndpoint ? "50%" : 0,
        bgcolor: isEndpoint ? C.primary : inRange ? C.tintBg : "transparent",
        color: isEndpoint ? "#fff" : C.ink,
        fontWeight: isEndpoint ? 600 : 400,
        "&:hover": { bgcolor: isEndpoint ? C.primary : C.tint2 },
      }}
    >
      {day.date()}
    </Box>
  );
}

function MonthGrid({ month, draftStart, draftEnd, onPick, nav }) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, height: 32 }}>
        {nav.side === "prev" ? (
          <IconButton size="small" onClick={nav.onClick}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {month.format("MMMM YYYY")}
        </Typography>
        {nav.side === "next" ? (
          <IconButton size="small" onClick={nav.onClick}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 34px)" }}>
        {WEEKDAY_LABELS.map((label) => (
          <Box
            key={label}
            sx={{ width: 34, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", color: C.textSecondary }}
          >
            {label}
          </Box>
        ))}
        {monthCells(month).map((day, i) => (
          <DayCell key={day ? day.format("YYYY-MM-DD") : `blank-${i}`} day={day} draftStart={draftStart} draftEnd={draftEnd} onPick={onPick} />
        ))}
      </Box>
    </Box>
  );
}

// Custom two-month range calendar (no MUI X Pro dependency) — pick a start
// day, then an end day; the days between get a light-blue band and the two
// endpoints get a solid circle, same look as a standard range picker.
// Picking never filters by itself — the user can keep re-picking start/end
// to adjust the range, and only Done (or closing the popover) applies it.
// A single day left picked with no end applies as a single-day filter
// (start === end, which the backend treats the same as sending `date`).
function ReportDateRangePicker({ startDate, endDate, onChange, disabled }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [leftMonth, setLeftMonth] = React.useState(() => (startDate ? dayjs(startDate) : dayjs()).startOf("month"));
  const [draftStart, setDraftStart] = React.useState(startDate || "");
  const [draftEnd, setDraftEnd] = React.useState(endDate || "");

  const openPicker = (e) => {
    setDraftStart(startDate || "");
    setDraftEnd(endDate || "");
    setLeftMonth((startDate ? dayjs(startDate) : dayjs()).startOf("month"));
    setAnchorEl(e.currentTarget);
  };
  const closePicker = () => setAnchorEl(null);

  // Picking never applies anything by itself — only Done (or closing the
  // popover) commits the draft, so the user can keep adjusting start/end
  // after a range is picked, before it actually filters the table.
  const handlePick = (dateStr) => {
    if (!draftStart || draftEnd) {
      setDraftStart(dateStr);
      setDraftEnd("");
      return;
    }
    if (dateStr < draftStart) {
      setDraftStart(dateStr);
      setDraftEnd("");
      return;
    }
    setDraftEnd(dateStr);
  };

  const handleClear = () => {
    setDraftStart("");
    setDraftEnd("");
    onChange("", "");
  };

  // Commits the draft on close — a single picked day applies as start === end,
  // a full range applies as-is, and nothing picked clears the filter.
  const handleDone = () => {
    if (draftStart) onChange(draftStart, draftEnd || draftStart);
    else onChange("", "");
    closePicker();
  };

  const label = !startDate
    ? t("portal.reports.filters.dateAny")
    : !endDate || endDate === startDate
    ? dayjs(startDate).format("MMM D, YYYY")
    : `${dayjs(startDate).format("MMM D, YYYY")} – ${dayjs(endDate).format("MMM D, YYYY")}`;

  return (
    <>
      <Button
        variant="outlined"
        onClick={openPicker}
        disabled={disabled}
        startIcon={<PageHeaderIcon src="/icons/ic_calender.svg" size={18} color={C.ink} />}
        sx={{
          textTransform: "none",
          borderRadius: 1.5,
          borderColor: "#e0e0e0",
          color: C.ink,
          fontWeight: startDate ? 600 : 400,
          backgroundColor: startDate ? C.tintBg : "#fff",
          px: 1.5,
          whiteSpace: "nowrap",
          "&:hover": { borderColor: "#e0e0e0", backgroundColor: startDate ? C.tintBg : "#f5f5f5" },
        }}
      >
        {label}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleDone}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 3 }}>
            <MonthGrid
              month={leftMonth}
              draftStart={draftStart}
              draftEnd={draftEnd}
              onPick={handlePick}
              nav={{ side: "prev", onClick: () => setLeftMonth((m) => m.subtract(1, "month")) }}
            />
            <MonthGrid
              month={leftMonth.add(1, "month")}
              draftStart={draftStart}
              draftEnd={draftEnd}
              onPick={handlePick}
              nav={{ side: "next", onClick: () => setLeftMonth((m) => m.add(1, "month")) }}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, pb: 1.5 }}>
          <Button size="small" onClick={handleClear} sx={{ textTransform: "none" }}>
            {t("portal.reports.filters.clear")}
          </Button>
          <Button size="small" onClick={handleDone} sx={{ textTransform: "none" }}>
            {t("portal.reports.filters.done")}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default ReportDateRangePicker;
